import { browser } from '$app/environment';
import { page } from '$app/stores';
import { PUBLIC_BETWATCH_API_ENDPOINT } from '$env/static/public';
import type {
  AddPriceAlertResponse,
  AllEventAlertsResponse,
  EventAlertInput,
  PriceAlertInput,
  PriceAlertsResponse,
  RemovePriceAlertResponse,
  UpdateEventAlertsResponse
} from '$types/races';
import { get } from 'svelte/store';

export async function updateEventAlerts(
  id: string,
  alerts: EventAlertInput[],
  { accessToken = '', fetch = window.fetch } = {}
) {
  //   if (!browser) return;
  const access_token = accessToken
    ? accessToken
    : get(page)?.data?.session?.access_token;
  if (!access_token) return;

  const response = (await fetch(PUBLIC_BETWATCH_API_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + access_token
    },
    body: JSON.stringify({
      query: `mutation UpdateAlerts($id: ID!, $alerts: [EventAlertInput!]!) {
            updateAlerts(id: $id, alerts: $alerts) {
              eventId
              alerts {
                trigger
                value
              }
            }
          }`,
      variables: {
        id,
        alerts
      }
    })
  })
    .then((resp) => resp.json())
    .catch((err) => {
      console.error(err);
      return undefined;
    })) as UpdateEventAlertsResponse;

  return response;
}

export async function getEventAlerts({
  accessToken = '',
  fetch = window.fetch,
  retriesLeft = 5,
  retryDelay = 1000
}) {
  //   if (!browser) return;
  const access_token = accessToken
    ? accessToken
    : browser
      ? get(page)?.data?.session?.access_token
      : null;
  if (!access_token) return;

  const response = (await fetch(PUBLIC_BETWATCH_API_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + access_token
    },
    body: JSON.stringify({
      query: `query GetAllEventAlerts {
          allEventAlerts {
            eventId
            meeting {
                track
                type
                location
            }
            alerts {
                trigger
                value
            }
          }
        }`
    })
  })
    .then((resp) => resp.json())
    .catch(async (err) => {
      console.error(err);
      if (retriesLeft > 0) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        return getEventAlerts({
          accessToken,
          fetch,
          retriesLeft: retriesLeft - 1,
          retryDelay: retryDelay * 2
        });
      }
      return undefined;
    })) as AllEventAlertsResponse;

  return response;
}

export async function getPriceAlerts({
  accessToken = '',
  fetch = window.fetch,
  retriesLeft = 5,
  retryDelay = 1000
}) {
  //   if (!browser) return;
  const access_token = accessToken
    ? accessToken
    : browser
      ? get(page)?.data?.session?.access_token
      : null;
  if (!access_token) return;

  const response = (await fetch(PUBLIC_BETWATCH_API_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + access_token
    },
    body: JSON.stringify({
      query: `query GetPriceAlerts {
        priceAlerts {
          alertType
          bookmakers
          createdAt
          eventId
          id
          priceIds
          priceType
          runnerId
          userId
          value
          variance
          persistent
        }
      }`
    })
  })
    .then((resp) => resp.json())
    .catch(async (err) => {
      console.error(err);
      if (retriesLeft > 0) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay));
        return getPriceAlerts({
          accessToken,
          fetch,
          retriesLeft: retriesLeft - 1,
          retryDelay
        });
      }
      return undefined;
    })) as PriceAlertsResponse;

  return response;
}

export async function createPriceAlert(
  alert: PriceAlertInput,
  extra?: { accessToken: string }
) {
  const access_token = extra?.accessToken
    ? extra.accessToken
    : get(page)?.data?.session?.access_token;
  if (!access_token) return;

  const response = (await fetch(PUBLIC_BETWATCH_API_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + access_token
    },
    body: JSON.stringify({
      query: `mutation AddPriceAlert($alert: AddPriceAlertInput!) {
        addPriceAlert(input: $alert) {
          id
          userId
          eventId
          runnerId
          priceIds
          alertType
          priceType
          bookmakers
          value
          variance
          persistent
          lastTriggeredPrice {
            priceId
            price
          }
          createdAt
          updatedAt
        }
      }`,
      variables: {
        alert: {
          ...alert,
          userId: alert.userId || get(page)?.data?.session?.user?.id
        }
      }
    })
  })
    .then((resp) => resp.json())
    .catch((err) => {
      console.error(err);
      return undefined;
    })) as AddPriceAlertResponse;

  // add to page store
  if (response?.data?.addPriceAlert?.id) {
    const pageAlerts = get(page)?.data?.priceAlerts;
    pageAlerts?.update((alerts) => {
      if (!alerts || !response.data) return;
      alerts.data?.priceAlerts?.push(response.data?.addPriceAlert);
      return alerts;
    });
  }

  return response;
}

export async function removePriceAlert(
  alertId: string,
  extra?: { accessToken: string }
) {
  const access_token = extra?.accessToken
    ? extra.accessToken
    : get(page)?.data?.session?.access_token;
  if (!access_token) return;

  const response = (await fetch(PUBLIC_BETWATCH_API_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + access_token
    },
    body: JSON.stringify({
      query: `mutation RemovePriceAlert($alert: RemovePriceAlertInput!) {
            removePriceAlert(input: $alert) {
                id
            }
          }`,
      variables: {
        alert: {
          id: alertId
        }
      }
    })
  })
    .then((resp) => resp.json())
    .catch((err) => {
      console.error(err);
      return undefined;
    })) as RemovePriceAlertResponse;

  // remove from page store
  if (response?.data?.removePriceAlert?.id) {
    const pageAlerts = get(page)?.data?.priceAlerts;
    pageAlerts?.update((alerts) => {
      if (!alerts?.data) return;
      alerts.data.priceAlerts = alerts.data?.priceAlerts.filter(
        (alert) => alert.id !== response.data?.removePriceAlert.id
      );
      return alerts;
    });
  }

  return response;
}
